import type {
  CreditNoteResponse,
  DeliveryNoteResponse,
  InvoiceResponse,
  QuotationResponse,
} from '@palaccount/contracts';

import type { LifecycleStep, StepState } from '@/components/document-list/types';

import { canConvert } from './quote-flow';

/**
 * Wave 3B — the SALES chain as each document sees it, in the shape the
 * shared `LifecycleStepper` draws. Sales Orders have their own
 * (`order-stepper.ts`, read from the server's derived stage); these three
 * read the links the row carries and nothing else, on the same three
 * conventions:
 *
 *  - A stage behind the current one with NO document behind it is SKIPPED
 *    (grey, struck through), never done: an invoice typed directly came from
 *    no quotation and no order, and the stepper must not claim it did.
 *  - A stage with a document behind it LINKS to it.
 *  - The next sensible act, where the row can do it, IS the act (D-09) —
 *    the receipt voucher for a posted, unpaid invoice; the conversions a
 *    quotation may still make.
 *
 * The five stage names are the order stepper's own keys (`orders.stage.*`),
 * so the chain reads the same on every sales list.
 */

const step = (
  key: string,
  state: StepState,
  extra: Partial<Pick<LifecycleStep, 'href' | 'action' | 'permission'>> = {},
): LifecycleStep => ({ key, labelKey: `orders.stage.${key}`, state, ...extra });

/** SAL-04 — Quotation → Sales order → Delivery → INVOICE → Payment. */
export function invoiceSteps(invoice: InvoiceResponse): LifecycleStep[] {
  const cancelled = invoice.status === 'cancelled';
  const paid = invoice.status === 'posted' && invoice.fullyPaid;
  const payable = invoice.status === 'posted' && !invoice.fullyPaid;
  return [
    // The invoice carries no quotation link, so the stage is unknown — and unknown is skipped, not done.
    step('quotation', 'skipped'),
    invoice.salesOrderId
      ? step('sales_order', 'done', { href: `/sales/orders?selected=${invoice.salesOrderId}` })
      : step('sales_order', 'skipped'),
    step('delivery', invoice.deliveryNoteCodes.length > 0 ? 'done' : 'skipped'),
    step('invoice', 'current'),
    step('payment', cancelled ? 'skipped' : paid ? 'done' : 'pending', {
      ...(payable
        ? {
            action: {
              labelKey: 'orders.stepAct.payment',
              to: `/accounting/receipts/new?partnerId=${invoice.partnerId}&invoiceId=${invoice.id}`,
            },
            permission: { permission: 'accounting.create', reasonKey: 'perm.cannot.createReceiptVouchers' },
          }
        : {}),
    }),
  ];
}

/**
 * SAL-01 — QUOTATION → Sales order → Delivery → Invoice → Payment. The two
 * conversions are the page's own acts (one POST, then the new document
 * opens), offered while the quotation may still convert (R4: accepted or
 * expired) and has not already.
 */
export function quotationSteps(
  quote: QuotationResponse,
  acts: { convertToOrder: () => void; convertToInvoice: () => void; busy: boolean },
): LifecycleStep[] {
  const convertible = canConvert(quote.status) && !quote.convertedOrderId && !quote.convertedInvoiceId;
  const convertAction = (labelKey: string, onAct: () => void) => ({
    action: { labelKey, onAct, disabled: acts.busy },
    permission: { permission: 'sales.create' as const, reasonKey: 'perm.cannot.convertQuotes' },
  });
  return [
    step('quotation', 'current'),
    quote.convertedOrderId
      ? step('sales_order', 'done', { href: `/sales/orders?selected=${quote.convertedOrderId}` })
      : step('sales_order', 'pending', convertible ? convertAction('quotes.stepAct.order', acts.convertToOrder) : {}),
    step('delivery', 'pending'),
    quote.convertedInvoiceId
      ? step('invoice', 'done', { href: `/sales/${quote.convertedInvoiceId}` })
      : step('invoice', 'pending', convertible ? convertAction('quotes.stepAct.invoice', acts.convertToInvoice) : {}),
    step('payment', 'pending'),
  ];
}

/**
 * SAL-03 — Quotation → Sales order → DELIVERY → Invoice → Payment. A note
 * always has its order, and the invoice is made FROM the order (SAL-02's
 * Create invoice), so the Invoice stage opens the order on the Sales Orders
 * page once the goods have left.
 */
export function deliverySteps(note: DeliveryNoteResponse): LifecycleStep[] {
  return [
    step('quotation', 'skipped'),
    step('sales_order', 'done', { href: `/sales/orders?selected=${note.salesOrderId}` }),
    step('delivery', 'current'),
    step('invoice', 'pending', {
      ...(note.status === 'posted'
        ? {
            action: { labelKey: 'dn.stepAct.invoice', to: `/sales/orders?selected=${note.salesOrderId}` },
            permission: { permission: 'sales.create', reasonKey: 'perm.cannot.createInvoices' },
          }
        : {}),
    }),
    step('payment', 'pending'),
  ];
}

const returnStep = (key: string, state: StepState, extra: Partial<Pick<LifecycleStep, 'href'>> = {}): LifecycleStep => ({
  key,
  labelKey: `returns.stage.${key}`,
  state,
  ...extra,
});

/**
 * SAL-05 — the return's OWN chain: Invoice → RETURN → Stock back → Credit
 * note / refund. Stock back is greyed when the goods do not come back
 * (`restock` off — damaged goods, BR-SAL-05-07); the last stage is done once
 * the credit stands against the invoice (a linked note credits it the moment
 * it posts) or the cash has left (`refundedAt`), and still pending on a
 * posted stand-alone note that has been neither applied nor refunded.
 */
export function creditNoteSteps(note: CreditNoteResponse): LifecycleStep[] {
  const posted = note.status === 'posted';
  const cancelled = note.status === 'cancelled';
  const settled = note.refundedAt !== null || (posted && note.invoiceId !== null);
  return [
    note.invoiceId ? returnStep('invoice', 'done', { href: `/sales/${note.invoiceId}` }) : returnStep('invoice', 'skipped'),
    returnStep('return', 'current'),
    returnStep('stockBack', !note.restock || cancelled ? 'skipped' : posted ? 'done' : 'pending'),
    returnStep('settlement', cancelled ? 'skipped' : settled ? 'done' : 'pending'),
  ];
}
